import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Stack } from "@UI/Components/Stack/Stack";
import { Flex } from "@UI/Components/Flex/Flex";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { useRollbar } from "@rollbar/react";
import { BugIcon } from "@UI/Components/Icons/BugIcon";
import { RouterLink } from "@Libs/Router/Components/RouterLink/RouterLink";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import messages from "./messages";

interface IBaseErrorBoundaryProps {
  children: React.ReactNode;
  // Optional Rollbar person identity to attribute errors to the current user
  person?: { id: string | number; username?: string; email?: string };
}

const BOUNDARY_CONTEXT = "BaseErrorBoundary" as const;

// Extract the top-most component from React's componentStack string
const topComponentFromStack = (componentStack?: string): string | undefined => {
  // componentStack example:
  // "\n    in Problematic (at SomeFile.tsx:12)\n    in Parent ..."
  const firstLine = componentStack
    ?.split("\n")
    .map((l) => l.trim())
    .find(Boolean);
  return firstLine?.replace(/^in\s+/, "");
};

// Build a rich payload for Rollbar with useful debugging info
const buildRollbarPayload = (
  error: Error,
  info?: { componentStack?: string },
  opts?: { personId?: string | number }
) => {
  // Access optional Error.cause without using 'any' to satisfy lint rules
  const causeValue = (error as Error & { cause?: unknown }).cause;
  const causeString = causeValue !== undefined ? String(causeValue) : undefined;

  return {
    // Identify the logical place in the app where this error originated
    context: BOUNDARY_CONTEXT,
    // Additional metadata goes into the "custom" payload in Rollbar
    custom: {
      // Tag a stable source to make filtering easy in Rollbar
      source: BOUNDARY_CONTEXT,
      personId: opts?.personId,
      url: typeof window !== "undefined" ? window.location.href : undefined,
      path:
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search}${window.location.hash}`
          : undefined,
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      time: new Date().toISOString(),
      react: {
        componentStack: info?.componentStack,
        topComponent: topComponentFromStack(info?.componentStack),
      },
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
        // include cause when available (Error.cause is not always present)
        cause: causeString,
      },
      referrer: typeof document !== "undefined" ? document.referrer : undefined,
      viewport:
        typeof window !== "undefined"
          ? {
              width: window.innerWidth,
              height: window.innerHeight,
              pixelRatio: window.devicePixelRatio,
            }
          : undefined,
    },
  } as const;
};

const FallbackUI = () => {
  return (
    <Stack
      role="alert"
      spacing="40px"
      alignItems="center"
      bg="sand.100"
      py="80px"
      mb="-35px"
      textAlign="center"
    >
      <Text as="h3">
        <FormattedMessage {...messages.header} />
      </Text>

      <BugIcon />

      <Stack spacing="0" alignItems="center">
        <Text color="gray.700" mb="0" sx={{ "text-wrap": "balance" }}>
          <FormattedMessage {...messages.title} />
        </Text>
        <Text color="gray.700" mb="0" sx={{ "text-wrap": "balance" }}>
          <FormattedMessage {...messages.subtitle} />
        </Text>
      </Stack>

      <Flex gap="15" flexDir={{ base: "column", md: "row" }}>
        <Button colorScheme="seaGreen" onClick={() => location.reload()}>
          <FormattedMessage {...messages.reload} />
        </Button>

        <RouterLink textDecoration="none" route="CONTACT_US" reloadDocument>
          <Button colorScheme="seaGreen" variant="outline">
            <FormattedMessage {...messages.contactSupport} />
          </Button>
        </RouterLink>
      </Flex>
    </Stack>
  );
};

export const BaseErrorBoundary: React.FC<IBaseErrorBoundaryProps> = (props) => {
  const { children, person } = props;

  const rollbar = useRollbar();
  const { currentUser, isSuccess: isCurrentUserLoaded } = useCurrentUser();

  // Queue to hold errors until we know the current user (to attribute personId)
  const pendingReports = React.useRef<
    Array<{ error: Error; info: { componentStack?: string } }>
  >([]);
  const flushTimerRef = React.useRef<number | null>(null);

  const flushPending = React.useCallback(
    (withPersonId?: string | number) => {
      const queue = pendingReports.current.splice(0);
      for (const { error, info } of queue) {
        const top = topComponentFromStack(info?.componentStack);
        const title = top
          ? `[${error.name}] ${error.message} @ ${top}`
          : `[${error.name}] ${error.message}`;
        rollbar.error(
          title,
          error,
          buildRollbarPayload(error, info, { personId: withPersonId })
        );
      }
    },
    [rollbar]
  );

  // If a person is provided or a current user is available, attach it so Rollbar items are attributed to the user
  React.useEffect(() => {
    const configuredPerson =
      person ||
      (isCurrentUserLoaded && currentUser
        ? {
            id: currentUser.id,
            email: currentUser.email,
            firstName: currentUser.firstName || undefined,
            locale: currentUser.locale,
            subscribed: currentUser.subscribed,
            countryId: currentUser.countryId,
            banned: currentUser.banned,
            lastSignInIp: currentUser.lastSignInIp,
          }
        : undefined);

    if (configuredPerson) {
      rollbar.configure({ payload: { person: configuredPerson } });
    }
  }, [rollbar, person, isCurrentUserLoaded, currentUser]);

  // Once the user is loaded, flush any queued errors with the correct personId
  React.useEffect(() => {
    if (isCurrentUserLoaded) {
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      flushPending(currentUser?.id);
    }
  }, [isCurrentUserLoaded, currentUser?.id, flushPending]);

  return (
    <ErrorBoundary
      fallbackRender={FallbackUI}
      onError={(error, info) => {
        if (process.env.NODE_ENV === "production") {
          // Wait to log until we know the user; queue and flush when loaded
          if (!isCurrentUserLoaded) {
            pendingReports.current.push({ error, info });
            if (flushTimerRef.current === null) {
              // Fallback: if user never loads (guest or API failure), flush after 5s without personId
              flushTimerRef.current = window.setTimeout(() => {
                flushPending();
                flushTimerRef.current = null;
              }, 5000);
            }
            return;
          }

          const top = topComponentFromStack(info?.componentStack);
          const title = top
            ? `[${error.name}] ${error.message} @ ${top}`
            : `[${error.name}] ${error.message}`;

          const personId = person?.id ?? currentUser?.id;
          // Use message as title, pass Error object so Rollbar captures stack, plus rich custom context
          rollbar.error(
            title,
            error,
            buildRollbarPayload(error, info, { personId })
          );
        }
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
